import React, { useState } from 'react';
import BudgetOperationHistory from './BudgetOperationHistory';
import QuickActionModal from './QuickActionModal';
import { getCompletedBudgetOperations, getPendingBudgetOperations } from '../api/index';
import { formatDateOnly } from '../utils/dateUtils';

const BudgetOperationsList = ({ operations, onEdit, onDelete, permissions, currentUser }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCell, setFilterCell] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [sortBy, setSortBy] = useState('fecha_peticion');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showHistory, setShowHistory] = useState(false);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'pending', 'completed'
  const [completedOperations, setCompletedOperations] = useState([]);
  const [pendingOperations, setPendingOperations] = useState([]);
  const [loading, setLoading] = useState(false);

  // Listas para filtros
  const allCells = [...new Set(operations.map(op => op.celula))].sort();
  const allMonths = [...new Set(operations.map(op => op.mes))].sort();

  // Calcular contadores reales desde las operaciones existentes
  const realPendingCount = operations.filter(op => !op.completed).length;
  const realCompletedCount = operations.filter(op => op.completed).length;

  // Determinar qué operaciones mostrar según la pestaña activa
  const getOperationsForTab = () => {
    switch (activeTab) {
      case 'pending':
        return pendingOperations;
      case 'completed':
        return completedOperations;
      default:
        return operations;
    }
  };

  // Filtrar y ordenar operaciones
  const filteredOperations = getOperationsForTab()
    .filter(operation => {
      const matchesSearch = 
        operation.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        operation.campana.toLowerCase().includes(searchTerm.toLowerCase()) ||
        operation.celula.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCell = !filterCell || operation.celula === filterCell;
      const matchesMonth = !filterMonth || operation.mes === filterMonth;
      
      return matchesSearch && matchesCell && matchesMonth;
    })
    .sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      // Manejar fechas
      if (sortBy === 'fecha_peticion') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      // Manejar números
      if (sortBy === 'subir_ppto_diario_a') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };


  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `$${parseFloat(amount).toLocaleString('es-ES', { minimumFractionDigits: 2 })}`;
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    return sortOrder === 'asc' ? (
      <svg className="w-4 h-4 text-[#0061A8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-[#0061A8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
      </svg>
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
      {/* Header con filtros */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Búsqueda */}
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Buscar por nombre, campaña o célula..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0061A8] focus:border-transparent"
              />
            </div>
          </div>

          {/* Filtros */}
          <div className="flex gap-3">
            <select
              value={filterCell}
              onChange={(e) => setFilterCell(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0061A8] focus:border-transparent"
            >
              <option value="">Todas las células</option>
              {allCells.map(cell => (
                <option key={cell} value={cell}>{cell}</option>
              ))}
            </select>

            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0061A8] focus:border-transparent"
            >
              <option value="">Todos los meses</option>
              {allMonths.map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Indicadores de filtros activos */}
        {(searchTerm || filterCell || filterMonth) && (
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-sm text-gray-500">Filtros activos:</span>
            {searchTerm && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Búsqueda: "{searchTerm}"
              </span>
            )}
            {filterCell && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Célula: {filterCell}
              </span>
            )}
            {filterMonth && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Mes: {filterMonth}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Pestañas de navegación */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('all')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center ${
              activeTab === 'all'
                ? 'border-[#0061A8] text-[#0061A8]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            Todas las Operaciones
            <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs font-medium">
              {operations.length}
            </span>
          </button>
          
          <button
            onClick={async () => {
              setActiveTab('pending');
              setLoading(true);
              try {
                const pending = await getPendingBudgetOperations();
                setPendingOperations(pending);
              } catch (error) {
                console.error('Error al cargar operaciones pendientes:', error);
              } finally {
                setLoading(false);
              }
            }}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center ${
              activeTab === 'pending'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Pendientes
            {loading && activeTab === 'pending' ? (
              <span className="ml-2 bg-orange-100 text-orange-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-600 inline-block"></div>
              </span>
            ) : (
              <span className="ml-2 bg-orange-100 text-orange-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                {realPendingCount}
              </span>
            )}
          </button>
          
          <button
            onClick={async () => {
              setActiveTab('completed');
              setLoading(true);
              try {
                const completed = await getCompletedBudgetOperations();
                setCompletedOperations(completed);
              } catch (error) {
                console.error('Error al cargar operaciones completadas:', error);
              } finally {
                setLoading(false);
              }
            }}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center ${
              activeTab === 'completed'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Completadas
            {loading && activeTab === 'completed' ? (
              <span className="ml-2 bg-green-100 text-green-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600 inline-block"></div>
              </span>
            ) : (
              <span className="ml-2 bg-green-100 text-green-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                {realCompletedCount}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Link Cuenta
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('fecha_peticion')}
              >
                <div className="flex items-center space-x-1">
                  <span>Fecha Petición</span>
                  <SortIcon field="fecha_peticion" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('celula')}
              >
                <div className="flex items-center space-x-1">
                  <span>Célula</span>
                  <SortIcon field="celula" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('nombre')}
              >
                <div className="flex items-center space-x-1">
                  <span>Nombre</span>
                  <SortIcon field="nombre" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('campana')}
              >
                <div className="flex items-center space-x-1">
                  <span>Campaña</span>
                  <SortIcon field="campana" />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Presupuesto Actual
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('subir_ppto_diario_a')}
              >
                <div className="flex items-center space-x-1">
                  <span>Subir Ppto Diario A:</span>
                  <SortIcon field="subir_ppto_diario_a" />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Comparación
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('mes')}
              >
                <div className="flex items-center space-x-1">
                  <span>Mes</span>
                  <SortIcon field="mes" />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Observaciones
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fechas de Aumento
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              {permissions.canEditAllClients && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredOperations.map((operation) => (
              <tr key={operation.id} className="hover:bg-gray-50 transition-colors duration-200">
                <td className="px-6 py-4 whitespace-nowrap">
                  {operation.link_cuenta ? (
                    <a
                      href={operation.link_cuenta}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#0061A8] hover:text-[#004d8a] transition-colors duration-200"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDateOnly(operation.fecha_peticion)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {operation.celula}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{operation.nombre}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 max-w-xs truncate" title={operation.campana}>
                    {operation.campana}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 max-w-xs truncate" title={operation.acciones}>
                    {operation.acciones || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                  {formatCurrency(operation.presupuesto_actual)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                  {formatCurrency(operation.subir_ppto_diario_a)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {operation.presupuesto_actual && operation.subir_ppto_diario_a ? (
                    <div className="flex flex-col space-y-1">
                      <div className="flex items-center space-x-2">
                        {parseFloat(operation.subir_ppto_diario_a) > parseFloat(operation.presupuesto_actual) ? (
                          <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17l9.2-9.2M17 17V7H7" />
                          </svg>
                        ) : parseFloat(operation.subir_ppto_diario_a) < parseFloat(operation.presupuesto_actual) ? (
                          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 7l-9.2 9.2M7 7v10h10" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14" />
                          </svg>
                        )}
                        <span className={`text-sm font-medium ${
                          parseFloat(operation.subir_ppto_diario_a) > parseFloat(operation.presupuesto_actual) 
                            ? 'text-green-600' 
                            : parseFloat(operation.subir_ppto_diario_a) < parseFloat(operation.presupuesto_actual)
                            ? 'text-red-600'
                            : 'text-gray-600'
                        }`}>
                          {((parseFloat(operation.subir_ppto_diario_a) - parseFloat(operation.presupuesto_actual)) / parseFloat(operation.presupuesto_actual) * 100).toFixed(1)}%
                        </span>
                      </div>
                      <span className={`text-xs ${
                        parseFloat(operation.subir_ppto_diario_a) > parseFloat(operation.presupuesto_actual) 
                          ? 'text-green-600' 
                          : parseFloat(operation.subir_ppto_diario_a) < parseFloat(operation.presupuesto_actual)
                          ? 'text-red-600'
                          : 'text-gray-600'
                      }`}>
                        {formatCurrency(parseFloat(operation.subir_ppto_diario_a) - parseFloat(operation.presupuesto_actual))}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">Sin datos</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {operation.mes}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 max-w-xs truncate" title={operation.observaciones}>
                    {operation.observaciones || '-'}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col gap-1">
                    {operation.fechas_aumento ? (
                      (() => {
                        // Parsear fechas_aumento si es una cadena JSON
                        let fechas = operation.fechas_aumento;
                        if (typeof fechas === 'string') {
                          try {
                            fechas = JSON.parse(fechas);
                          } catch (e) {
                            console.error('Error al parsear fechas_aumento:', e);
                            return <span className="text-gray-400">Error al mostrar fechas</span>;
                          }
                        }
                        
                        // Verificar si hay fechas para mostrar
                        if (Array.isArray(fechas) && fechas.length > 0) {
                          return fechas.map((fecha, index) => (
                            <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {formatDateOnly(fecha)}
                            </span>
                          ));
                        } else {
                          return <span className="text-gray-400">-</span>;
                        }
                      })()
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {operation.completed ? (
                    <div className="flex flex-col space-y-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Completada
                      </span>
                      {operation.completed_at && (
                        <span className="text-xs text-gray-500">
                          {formatDateOnly(operation.completed_at)}
                        </span>
                      )}
                      {operation.completed_by && (
                        <span className="text-xs text-gray-500">
                          por {operation.completed_by}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Pendiente
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => {
                        setSelectedOperation(operation);
                        setShowQuickAction(true);
                      }}
                      className="text-green-600 hover:text-green-800 transition-colors duration-200"
                      title="Acción rápida"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        setSelectedOperation(operation);
                        setShowHistory(true);
                      }}
                      className="text-purple-600 hover:text-purple-800 transition-colors duration-200"
                      title="Ver historial"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                    {permissions.canEditAllClients && (
                      <>
                        <button
                          onClick={() => onEdit(operation)}
                          className="text-[#0061A8] hover:text-[#004d8a] transition-colors duration-200"
                          title="Editar operación"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => onDelete(operation.id)}
                          className="text-red-600 hover:text-red-800 transition-colors duration-200"
                          title="Eliminar operación"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Estado vacío */}
      {filteredOperations.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {operations.length === 0 ? 'No hay operaciones registradas' : 'No se encontraron operaciones'}
          </h3>
          <p className="text-gray-500">
            {operations.length === 0 
              ? 'Comienza agregando tu primera operación de aumento de presupuesto.'
              : 'Intenta ajustar los filtros para encontrar lo que buscas.'
            }
          </p>
        </div>
      )}

      {/* Footer con información */}
      {filteredOperations.length > 0 && (
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>
              Mostrando {filteredOperations.length} de {operations.length} operaciones
            </span>
            <span>
              Total presupuesto: {formatCurrency(
                filteredOperations.reduce((sum, op) => sum + (parseFloat(op.subir_ppto_diario_a) || 0), 0)
              )}
            </span>
          </div>
        </div>
      )}

      {/* Modal de Historial */}
      {showHistory && selectedOperation && (
        <BudgetOperationHistory
          operationId={selectedOperation.id}
          operationName={`${selectedOperation.nombre} - ${selectedOperation.campana}`}
          onClose={() => {
            setShowHistory(false);
            setSelectedOperation(null);
          }}
        />
      )}

      {/* Modal de Acción Rápida */}
      {showQuickAction && selectedOperation && (
        <QuickActionModal
          operation={selectedOperation}
          currentUser={currentUser}
          onClose={() => {
            setShowQuickAction(false);
            setSelectedOperation(null);
          }}
          onSuccess={() => {
            // Recargar la página para mostrar los cambios actualizados
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

export default BudgetOperationsList;
